import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { UserPreferences } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";

export interface GoalFilters {
  goalTypeFilter?: "current" | "future" | "all";
  categoryFilter?: string;
  achievementFilter?: "all" | "active" | "achieved";
  viewMode?: "grid" | "list";
  /** Which goals the Goal Category Overview charts include. */
  overviewStatusFilter?: "active" | "achieved";
}

const OVERVIEW_STATUS_VALUES = new Set(["active", "achieved"]);

const GOAL_TYPE_VALUES = new Set(["current", "future", "all"]);
const ACHIEVEMENT_VALUES = new Set(["all", "active", "achieved"]);
const VIEW_MODE_VALUES = new Set(["grid", "list"]);

function sanitizeFilters(raw: unknown): GoalFilters {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  return {
    goalTypeFilter:
      typeof r.goalTypeFilter === "string" && GOAL_TYPE_VALUES.has(r.goalTypeFilter)
        ? (r.goalTypeFilter as GoalFilters["goalTypeFilter"])
        : undefined,
    categoryFilter:
      typeof r.categoryFilter === "string" ? r.categoryFilter : undefined,
    achievementFilter:
      typeof r.achievementFilter === "string" && ACHIEVEMENT_VALUES.has(r.achievementFilter)
        ? (r.achievementFilter as GoalFilters["achievementFilter"])
        : undefined,
    viewMode:
      typeof r.viewMode === "string" && VIEW_MODE_VALUES.has(r.viewMode)
        ? (r.viewMode as GoalFilters["viewMode"])
        : undefined,
    overviewStatusFilter:
      typeof r.overviewStatusFilter === "string" && OVERVIEW_STATUS_VALUES.has(r.overviewStatusFilter)
        ? (r.overviewStatusFilter as GoalFilters["overviewStatusFilter"])
        : undefined,
  };
}

const STORAGE_KEY = "goalsFilterPreferences";

export function useGoalFilters(defaultFilters: GoalFilters = {}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [filters, setFilters] = useState<GoalFilters>(defaultFilters);
  const initializedRef = useRef(false);
  const isInitializingRef = useRef(true);
  // Changes the user makes before hydration finishes; re-applied on top of
  // the stored filters so an early click is neither lost nor unsaved.
  const pendingUpdatesRef = useRef<Partial<GoalFilters>>({});

  const { data: preferences, isLoading } = useQuery<UserPreferences>({
    queryKey: ["/api/user/preferences"],
  });

  // Serialize server saves: always send the latest snapshot, one request at a
  // time, so out-of-order responses can't overwrite a newer selection.
  const latestSnapshotRef = useRef<GoalFilters | null>(null);
  const saveInFlightRef = useRef(false);
  const flushSave = useCallback(async () => {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    try {
      while (latestSnapshotRef.current) {
        const snapshot = latestSnapshotRef.current;
        latestSnapshotRef.current = null;
        const res = await apiRequest("PATCH", "/api/user/preferences", { goalFilters: snapshot });
        const data = await res.json();
        queryClient.setQueryData(["/api/user/preferences"], data);
      }
    } catch (error) {
      console.error("Failed to save goal filters:", error);
    } finally {
      saveInFlightRef.current = false;
      if (latestSnapshotRef.current) void flushSave();
    }
  }, []);
  const saveGoalFilters = useCallback((goalFilters: GoalFilters) => {
    latestSnapshotRef.current = goalFilters;
    void flushSave();
  }, [flushSave]);

  // Re-initialize when the authenticated user changes.
  useEffect(() => {
    initializedRef.current = false;
    isInitializingRef.current = true;
    pendingUpdatesRef.current = {};
  }, [userId]);

  useEffect(() => {
    if (isLoading || initializedRef.current) return;

    initializedRef.current = true;

    try {
      let storedFilters: GoalFilters | undefined;

      if (preferences?.goalFilters) {
        storedFilters = sanitizeFilters(preferences.goalFilters);
      } else {
        const stored = localStorage.getItem(userLsKey(STORAGE_KEY, userId));
        if (stored) {
          storedFilters = sanitizeFilters(JSON.parse(stored));
        }
      }

      const pending = pendingUpdatesRef.current;
      const hasPending = Object.keys(pending).length > 0;

      if (storedFilters || hasPending) {
        const merged = { ...defaultFilters, ...storedFilters, ...pending };
        setFilters(merged);
        if (hasPending) {
          // Persist choices made before hydration completed.
          try {
            localStorage.setItem(userLsKey(STORAGE_KEY, userId), JSON.stringify(merged));
          } catch {}
          saveGoalFilters(merged);
        }
      }
      pendingUpdatesRef.current = {};

      isInitializingRef.current = false;
    } catch (error) {
      console.error("Failed to load goal filters:", error);
      isInitializingRef.current = false;
    }
  }, [preferences, isLoading, userId]);

  const updateFilters = useCallback((newFilters: Partial<GoalFilters>) => {
    if (isInitializingRef.current) {
      pendingUpdatesRef.current = { ...pendingUpdatesRef.current, ...newFilters };
    }
    setFilters((currentFilters) => {
      const updated = { ...currentFilters, ...newFilters };

      if (isInitializingRef.current) {
        return updated;
      }

      try {
        localStorage.setItem(userLsKey(STORAGE_KEY, userId), JSON.stringify(updated));
      } catch (error) {
        console.error("Failed to save goal filters:", error);
      }
      saveGoalFilters(updated);

      return updated;
    });
  }, [saveGoalFilters, userId]);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
    try {
      saveGoalFilters({});
      localStorage.removeItem(userLsKey(STORAGE_KEY, userId));
    } catch (error) {
      console.error("Failed to reset goal filters:", error);
    }
  }, [defaultFilters, saveGoalFilters]);

  return { filters, updateFilters, resetFilters, isLoading };
}
